// Blueprint: forge/blueprint/bff/architecture/001-bff-auth.md
import { notifySessionExpired, shouldReactTo401 } from './session-expiry';

/*
 * Standalone-only global 401 interceptor. Mirrors the Enterprise Hub host pattern (a global
 * XMLHttpRequest-prototype patch), extended to also cover window.fetch so every request on the
 * page — the shell's own calls AND child-MFE calls — is watched. On a same-origin 401 from an
 * authenticated request it calls the session-expiry bridge, which drops the user to the login
 * screen. Installed once at bootstrap (index.tsx); never wired into the embedded MFE.
 */

// Idempotent-install guard (dev HMR / double import must not double-patch the globals).
let installed = false;

// Saved originals so tests can restore the globals.
let originalFetch: typeof window.fetch | null = null;
let originalOpen: typeof XMLHttpRequest.prototype.open | null = null;
let originalSend: typeof XMLHttpRequest.prototype.send | null = null;

// Per-instance request URL captured at open() time, read on the load event.
const xhrUrls = new WeakMap<XMLHttpRequest, string>();

function react(status: number, url: string): void {
    if (status === 401 && shouldReactTo401(url)) {
        notifySessionExpired();
    }
}

function resolveFetchUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') {
        return input;
    }
    if (input instanceof URL) {
        return input.href;
    }
    return input.url;
}

function patchFetch(): void {
    originalFetch = window.fetch;
    const original = window.fetch.bind(window);
    window.fetch = async (input, init) => {
        const response = await original(input, init);
        // Read only .status — never consume the body (that would break callers).
        react(response.status, resolveFetchUrl(input));
        return response;
    };
}

function patchXhr(): void {
    originalOpen = XMLHttpRequest.prototype.open;
    originalSend = XMLHttpRequest.prototype.send;
    /*
     * Forwarders: cast to a permissive shape so we pass the caller's exact args straight
     * through. Typing the overrides with Parameters<XMLHttpRequest['open']> would make them
     * non-assignable to the DOM's overloaded `open` (its 2-arg overload forbids a required
     * `async`), which fails the build. `unknown[]` (never `any`) keeps this type-safe.
     */
    const savedOpen = originalOpen as (...args: unknown[]) => void;
    const savedSend = originalSend as (...args: unknown[]) => void;

    XMLHttpRequest.prototype.open = function open(
        this: XMLHttpRequest,
        method: string,
        url: string | URL,
        ...rest: unknown[]
    ) {
        xhrUrls.set(this, typeof url === 'string' ? url : url.href);
        savedOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function send(
        this: XMLHttpRequest,
        body?: Document | XMLHttpRequestBodyInit | null
    ) {
        this.addEventListener('load', () => {
            const url = xhrUrls.get(this);
            // An XHR with no captured URL can't be classified — never treat it as session expiry.
            if (url === undefined) {
                return;
            }
            react(this.status, url);
        });
        savedSend.call(this, body);
    };
}

export function installUnauthorizedInterceptor(): void {
    if (installed) {
        return;
    }
    installed = true;
    patchFetch();
    patchXhr();
}

// Test-only: restore the patched globals and clear the install guard.
export function uninstallUnauthorizedInterceptorForTests(): void {
    if (originalFetch) {
        window.fetch = originalFetch;
    }
    if (originalOpen) {
        XMLHttpRequest.prototype.open = originalOpen;
    }
    if (originalSend) {
        XMLHttpRequest.prototype.send = originalSend;
    }
    installed = false;
    originalFetch = null;
    originalOpen = null;
    originalSend = null;
}
